import { UserContext } from "../Definitions/UserContext";

import LayoutNode from "../Definitions/LayoutNode";
import { AsyncAjaxGet, AsyncAjaxPatch, AsyncAjaxPost } from "./AsyncAjaxService";
import { PublicAsyncAjaxGet, PublicAsyncAjaxPost } from "./AsyncAjaxPublicService";
import { utils } from "../../utils/Utils";
import { getCookie } from "./CookieUtils";
import { GetPreviewCluster } from "./WindowUtils";
import { ProductPlaceHolder } from "../Binding/BusinessPlaceholderData";
import { data } from "jquery";


// Safe clone function that handles objects with functions and circular references
// Uses JSON serialization to avoid structuredClone errors with Axios responses
function safeClone(data: any): any {
    if (data === null || data === undefined) {
        return data;
    }
    try {
        return JSON.parse(JSON.stringify(data));
    } catch (e) {
        console.warn('Failed to clone data, returning original:', e);
        return data;
    }
}

const VagaroAPIEnum = {
    getMerchantRoot: "merchants/",
    getBusinessHours: "/workinghours",
    getSocialLinks: "/sociallink",
    getServices: "/services",
    getServiceCategories: "/services/servicecategory",
    getClassCategories: "/classes/classcategory",
    getFacility: "/facilities",
    getReviews: "/reviews",
    getStaff: "/staffs",
    getProducts: "/products/search",
    getProductTypes: "/products/producttype",
    getProductCategories: "/products/categorys",
    getProductBrands: "/products/brand",
    getPhotos: "/photos",
    getDailyDeals: "/dailydeals",
    GetGiftCertificates: "/giftcards",
    getBusinessMap: "/maps",
    getBusinessMemberships: "/memberships",
    getBusinessPackages: "/packages",
    getReviewPercentage: "/reviews/reviewpercentage",
    getProductDetails: "/products/detail",
    getProductBundleDetails: "/products/bundle/",
    getCartCount: "/addtocart/getcartcount",
    getPromoCodes: "/promotions/discount",
    updateToken: "Token/generatetoken",
    getForms: "/form",
    getFormList: "/form/list",
    getBusinessLogo: "/businesslogo",
    getBusinessLocation: "mysite/distance",
    getBusinessName: "mysite/merchantname",
    getHouseCallCity: "mysite/housecallcities",
    getClustorBusinessData: "mysite/clustorbusinessdata",
    getBusinessHeroNickName: "mysite/nickname",
    getNicknameURL: "mysite/nickurl",
    getListingOnlyBusinesses: "mysite/getlistingonlybusinesses",
    getAdminServices: "mysite/getadminservices",
    getNearbyLocation: "merchants/mysite/nearestlocation",
    getFacilityFilters: "public/search/facilities",
    getAdminSearchServices:"public/search/admin/services",
    searchLocationSearch: "public/search/location/search",
    searchLocationPlace: "public/search/location/place",
    searchListing: "public/search/listing"
};


const businessDataCache: { [key: string]: BusinessDataCacheObject } = {};

enum CacheState {
    InProgress,
    Completed,
    Failed
}

enum ChacheMethod {
    GET,
    POST,
    // PUT,
    // DELETE
}

type BusinessDataCacheObject = {
    timestamp: number;
    responseData: any;
    cacheState: CacheState;
    subscribers: BusinessDataCacheSubscriber[];

}

type BusinessDataCacheSubscriber = {
    onComplete?: Function;
    onFailure?: Function;
}

(window as any).clearCachedResponses = () => {

    for (let key in businessDataCache) {
        delete businessDataCache[key];
    }
}

async function AsyncChache(method: ChacheMethod, url: string, onComplete: Function, onFailure: Function, context: UserContext, payload: any = null, legacy: boolean = false, ajaxGetFn: Function = AsyncAjaxGet, ajaxPostFn: Function = AsyncAjaxPost): Promise<void> {

    await ValidateToken(context);

    let body = "";
    if (payload)
        body = (typeof payload === "string") ? payload : JSON.stringify(payload);

    let key = url + context.businessId + body;
    let cacheObject = businessDataCache[key];


    if (!cacheObject || cacheObject.timestamp + 5 * 60 * 1000 < Date.now()) {

        // frist request, inistantiate the cache object and make an ajax call
        cacheObject = {
            timestamp: Date.now(),
            responseData: null,
            cacheState: CacheState.InProgress,
            subscribers: [{ onComplete, onFailure }]
        };

        businessDataCache[key] = cacheObject;

        const onSuccess = (data: any) => {
            cacheObject!.cacheState = CacheState.Completed;
            cacheObject!.responseData = data;
            cacheObject!.subscribers.forEach(subscriber => {
                try {
                    if (subscriber.onComplete) {
                        subscriber.onComplete(safeClone(data));
                    }
                } catch (e) {
                    console.warn('Cache subscriber onComplete error:', e);
                }
            });
            cacheObject!.subscribers = [];
        }

        const onError = (data: any) => {
            cacheObject!.cacheState = CacheState.Failed;
            cacheObject!.responseData = data;
            cacheObject!.subscribers.forEach(subscriber => {
                try {
                    if (subscriber.onFailure) {
                        subscriber.onFailure(safeClone(data));
                    }
                } catch (e) {
                    console.warn('Cache subscriber onFailure error:', e);
                }
            });
            cacheObject!.subscribers = [];

        }

        try {
            switch (method) {
                case ChacheMethod.GET:
                    return await ajaxGetFn(
                        url,
                        context.accessToken,
                        onSuccess,
                        onError,
                        legacy ? undefined : context.businessId,
                        legacy ? undefined : context.userId
                    );
                case ChacheMethod.POST:
                    return await ajaxPostFn(
                        url,
                        payload,
                        context.accessToken,
                        onSuccess,
                        onError,
                        legacy ? undefined : context.businessId,
                        legacy ? undefined : context.userId
                    );
                default:
                    throw new Error("Chache method not implemented: " + method);
            }
        } catch (e) {
            console.warn('Cache AJAX call failed:', e);
            onError(e);
        }

    }

    else if (cacheObject.cacheState === CacheState.InProgress) {

        // request in progress, just subscribe to current event
        return new Promise((resolve, reject) => {
            cacheObject.subscribers.push({
                onComplete: (data: any) => {
                    // call user’s callback
                    onComplete?.(data);
                    // resolve the promise for await
                    resolve(data);
                },
                onFailure: (err: any) => {
                    onFailure?.(err);
                    reject(err);
                },
            });
        });
    }

    else if (cacheObject.cacheState === CacheState.Completed) { // the data is already here

        return new Promise((resolve) => {
            onComplete( safeClone(cacheObject.responseData));
            resolve(safeClone(cacheObject.responseData));
        });
    }

    else if (cacheObject.cacheState === CacheState.Failed) { // something is wrong with this request

        return new Promise((resolve, reject) => {
            onFailure(safeClone(cacheObject.responseData));
            reject(safeClone(cacheObject.responseData));
        });
    }

}

async function AsyncGetBusinessInformation(siteData: UserContext, url: string, onComplete?: Function, onFailure?: Function) {

    await ValidateToken(siteData);

    const wrappedComplete = (data: any) => {
        if (onComplete) {
            const newData = data?.data?.data ?? null;
            onComplete(newData);
        }

    }

    const wrappedOnFailure = (data: any) => {
        if (onFailure) {
            onFailure(data);
        }

    }

    await AsyncChache(ChacheMethod.GET, url, wrappedComplete, wrappedOnFailure, siteData);
    //     await AsyncAjaxGet(
    //         url,
    //         siteData.accessToken,
    //         function onSuccess(data: any) {
    //             if (onComplete) {
    //                 const newData = data?.data?.data ?? null;
    //                 onComplete(newData);
    //             }
    //         },
    //         function OnError(data: any) {
    //             if (onFailure) {
    //                 onFailure(data);
    //             }

    //         },
    //         siteData.businessId
    //     );
    // }
}

async function AsyncGetNicknameURL(siteData: UserContext, url: string, onComplete?: Function, onFailure?: Function) {

    await ValidateToken(siteData);

    await AsyncAjaxGet(
        url,
        siteData.accessToken,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }

        },
        siteData.encryptMID
    );
}


export async function AsyncGetLocation(siteData: UserContext, latitude: number, longitude: number, IpAddress: string, url: string, isLocationControl: boolean, GetLocation_Success?: Function, GetLocation_Fail?: Function) {

    await ValidateToken(siteData);
    const Requestdata: any = {};
    Requestdata.PromoCodeIDs = "";
    Requestdata.latitude = (utils.isNotEmptyOrWhiteSpace(latitude) ? latitude : 0);
    Requestdata.longitude = (utils.isNotEmptyOrWhiteSpace(longitude) ? longitude : 0);
    Requestdata.IpAddress = (utils.isNotEmptyOrWhiteSpace(IpAddress) ? IpAddress : "");
    Requestdata.MongoJSON = "";
    Requestdata.IsGroupCall = false;
    Requestdata.IsLocationControl = isLocationControl;

    await AsyncChache(ChacheMethod.POST, url, GetLocation_Success ? (data: any) => GetLocation_Success(drillDownToData(data, 2)) : () => { }, GetLocation_Fail ? GetLocation_Fail : () => { }, siteData, Requestdata);
}

async function AsyncGetBusinessHouseCallCity(siteData: UserContext, businessId: string, url: string, GetBusinessHouseCallCity_Success?: Function, GetBusinessHouseCallCity_Fail?: Function) {

    await ValidateToken(siteData);


    await AsyncChache(ChacheMethod.GET, url, GetBusinessHouseCallCity_Success ? (data: any) => GetBusinessHouseCallCity_Success(drillDownToData(data, 2)) : () => { }, GetBusinessHouseCallCity_Fail ? GetBusinessHouseCallCity_Fail : () => { }, siteData);

}

export async function GetPromoCodes(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = userContext.businessId + VagaroAPIEnum.getPromoCodes;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}


// const ids = [
// "9sKNxELkCB5b5eNkKkqfVw==","RYNV8Yoq0H0fGdwNv7chvA==","NDHh3blWleM~5~6D7sJQfQ==","9hRhjIfnFiN5PETjoXL3lw==",
// "Wv7doPobVsu~IDHL4hHHzg=="


// ]
const drillDownToData = (data: any, num: number) => {

    for (let i = 0; i < num; i++) {
        if (data.data)
            data = data.data;
        else
            return null;
    }

    return data;
}



export async function GetProductDetails(userContext: UserContext, masterId: string, productId: string, onComplete?: Function, onFailure?: Function) {


    let params = `?InventoryId=${productId}&InventoryMasterId=${masterId}`;


    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProductDetails + params;



    await AsyncChache(ChacheMethod.GET, url, (data: any) => {

        if (data?.data?.message == "No Record Found") {

            let copy = { ...ProductPlaceHolder } as any
            copy.isStenciled = false;
            copy.noRecordsFound = true;
            onComplete && onComplete(copy);
        }
        else if (onComplete)
            onComplete(data?.data?.data);
    }, onFailure ? onFailure : () => { }, userContext, undefined, true)//userContext.businessId);




}

export async function GetProductBundleDetails(userContext: UserContext, productId: string, onComplete?: Function, onFailure?: Function) {

    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProductBundleDetails + productId;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

async function CommonReponse(siteData: UserContext, onComplete?: Function, onFailure?: Function) {
    if (onComplete) {
        onComplete([]);
    }
}

async function GetBusinessServices(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    if (!userContext.allowMultiLoc)
        userContext.allowMultiLoc = false;
    await ValidateToken(userContext);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    var cookieValue = getCookie("Referral") != "" ? true : false;
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getServices + "?IsEditor=" + IsEditor + "&Referral=" + cookieValue + "&IsAllowMultilocation=" + userContext.allowMultiLoc;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

async function GetBusinessServiceCategories(userContext: UserContext, businessId: string, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);

    const url = VagaroAPIEnum.getMerchantRoot + businessId + VagaroAPIEnum.getServiceCategories;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}


async function GetBusinessClassCategories(userContext: UserContext, businessId: string, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);

    const url = VagaroAPIEnum.getMerchantRoot + businessId + VagaroAPIEnum.getClassCategories;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBusinessMap(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getBusinessMap;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function getProductBrands(userContext: UserContext, onComplete?: Function, onFailure?: Function, filter: string[] = []) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProductBrands;
    const content = {
        "BrandlistFilter": filter
    }

    await AsyncChache(ChacheMethod.POST, url, onComplete ? onComplete : () => { }, onFailure ? onFailure : () => { }, userContext, content);

}

export async function getProductTypes(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProductTypes;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function getProductCategories(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProductCategories;
    await AsyncChache(ChacheMethod.GET, url, (data: any) => { onComplete && onComplete(data?.data?.data) }, onFailure ? onFailure : () => { }, userContext, undefined, true);

}


export async function ValidateToken(userContext: UserContext) {

    let expire = (userContext.tokenExpiresAt ?? 0) - 1000;

    let current = Date.now();

    // console.log("Validating...",userContext);
    // console.log(new Date(expire??0)," vs current:",new Date(current));

    if (!expire || current >= expire) {

        await UpdateAccessToken(userContext);

    }



    return true;
}

export async function GetBusinessLogoURL(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {




    await ValidateToken(siteData);

    const rootURL = "merchants/" + siteData.businessId + VagaroAPIEnum.getBusinessLogo;

    await AsyncChache(
        ChacheMethod.GET,
        rootURL,

        function onSuccess(data: any) {

            let imgPath = data?.data?.data?.imagePath ?? "";

            if (onComplete) {
                onComplete(imgPath);
            }
        },
        function OnError(data: any) {

            if (onFailure) {
                onFailure("");
            }
        }, siteData
    );
}

export async function UpdateAccessToken(userContext: UserContext) {

    userContext.accessToken = "eyJhbGciOiJSUzI1NiIsImtpZCI6IkQ3RTVCRkQ5QTg0REQ4QTgxQTU0QTY0NzlCOEQyNDRGREM0MDYxNTRSUzI1NiIsInR5cCI6ImF0K2p3dCIsIng1dCI6IjEtV18yYWhOMktnYVZLWkhtNDBrVDl4QVlWUSJ9.eyJuYmYiOjE3NDMxMzY5NDUsImV4cCI6MTc0MzE0MDU0NSwiaXNzIjoiaHR0cHM6Ly9hcy1kZXYtMDQtaWRzLmF6dXJld2Vic2l0ZXMubmV0IiwiY2xpZW50X2lkIjoiYTRlNTU0ODUtYmY0Zi00ZmU4LTk4ZjEtZjU1NTc0YjlhYmJkIiwiaWF0IjoxNzQzMTM2OTQ1LCJzY29wZSI6WyJ2YWdhcm9fdjJfYXBpIl19.RQgokH6oPgCNED2YjK8jzRu8SM96AEncUfdIsyPu4uaBnDO3DxqLxC4S8mfLlNovHEA7W4GNAxuj3zn4j54UE20e0AM6rOlRqR-KsAAAv6oMeMhTlaIOp5A8wSZ8GxDRCId71aMrUKze-mFZIVSE4mzavxKbpdBtuQuzwPcjdv4izqxpUPr5U4DsQYeDYseNBgq_pXV2efyJAS3TVDJU1r99ViOd-8fxj37VwzqXVErkr83XG7QZKP3Y9VnhJjRw6-2kzHbT0InMXcfxJysmNMDtbI6NM9WQQOsMkcSTlGxJlbdD-pC3G1L7hbzGRCb7vhuUtKGOoZHlT0MSo3BImQ";
    let expiresIn = 3600;
    let newDate = new Date();
    userContext.tokenExpiresAt = newDate.getTime() + (expiresIn / 2) * 1000;

    /* const url = VagaroAPIEnum.updateToken;
    let timeout = (window as any).vwbaccesstimeout;
    if (timeout && Date.now() < timeout) {
        return;
    }

    (window as any).vwbaccesstimeout = Date.now() + 10000;

    await AsyncAjaxPost(
        url,
        "",
        userContext.accessToken,
        function onSuccess(data: any) {

            const token = data?.data?.access_token;
            const expiresIn = data?.data?.expires_in;
            if (token && expiresIn) {
                userContext.accessToken = token;
                const newDate = new Date();
                userContext.tokenExpiresAt = newDate.getTime() + (expiresIn / 2) * 1000;
            }
            else {
                console.warn("Failed to update access token");
            }
        },
        function OnError(data: any) {
            console.warn("Failed to update access token", data);
        }

    ) */

}


export async function GetBusinessProducts(userContext: UserContext, onComplete?: Function, onFailure?: Function, pageNumber = 1, pageSize = 50, productTypes: string = "", brandFilters: string[] = [], optionFilters: string[] = [], optionSplitString = "|:|", searchText = "") {

    if (!userContext.allowMultiLoc)
        userContext.allowMultiLoc = false;

    // var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getProducts;
    await ValidateToken(userContext);

    let optionString = "";
    for (let i = 0; i < optionFilters.length; i++) {

        if (i > 0)
            optionString += ",";
        let split = optionFilters[i].split(optionSplitString);

        optionString += `{\"${split[0].replace(/"/g, '\\"')}\":\"${split[1].replace(/"/g, '\\"')}\"}`;

    }

    const body = {

        "pageIndex": pageNumber,
        "pageSize": pageSize,
        "productTypeList": productTypes,
        "brandIds": brandFilters.join(","),
        "searchText": searchText,
        "options": optionString,


    }

    await AsyncAjaxPost(
        url,
        JSON.stringify(body),
        userContext.accessToken,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }

        }
    );

}

export async function GetGiftCertificates(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    if (!userContext.allowMultiLoc)
        userContext.allowMultiLoc = false;
    await ValidateToken(userContext);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.GetGiftCertificates + "?IsEditor=" + IsEditor + "&IsAllowMultilocation=" + userContext.allowMultiLoc;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function getDailyDeals(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getDailyDeals;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBusinessGallery(userContext: UserContext, onComplete?: Function, onFailure?: Function, pageNumber = 1, pageSize = 500) {
    await ValidateToken(userContext);
    var url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getPhotos + "?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    var LoginDetails = utils.ReadCookie();
    if (LoginDetails.EncUid !== undefined && LoginDetails.EncUid !== "" && LoginDetails.EncUid !== null && LoginDetails.EncUid !== 0) {
        url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getPhotos + "?EncUserID=" + LoginDetails.EncUid + "&pageNumber=" + pageNumber + "&pageSize=" + pageSize;
    }
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBusinessDetails(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}



export async function GetBusienssHours(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getBusinessHours;
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetFacilityInformation(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getFacility;
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetBusinessSocialLinks(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getSocialLinks;
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetBusinessReviews(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void, pageSize = 20, page = 1, sort = "1", type = "1", SPID = "U2FsdGVkX18EI4k80FtcVtq4PzxA94OPqaGqyI1vxNM=") {
    await ValidateToken(siteData);
    var url;
    if (SPID != "U2FsdGVkX18EI4k80FtcVtq4PzxA94OPqaGqyI1vxNM=") {
        url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getReviews + "?pageSize=" + pageSize + "&pageNumber=" + page + "&filterType=" + type + "&sortType=" + sort + "&SPID=" + SPID;
    }
    else {
        url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getReviews + "?pageSize=" + pageSize + "&pageNumber=" + page + "&filterType=" + type + "&sortType=" + sort;
    }
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetBusinessStaff(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getStaff;
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetBusinessMemberships(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    if (!userContext.allowMultiLoc)
        userContext.allowMultiLoc = false;
    await ValidateToken(userContext);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getBusinessMemberships + "?IsEditor=" + IsEditor + "&IsAllowMultilocation=" + userContext.allowMultiLoc;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBuseinssesInDistanceForRenters(userContext: UserContext, latitude: number, longitude: number, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessLocation;
    await AsyncGetLocation(userContext, latitude, longitude, "", url, true, onComplete, onFailure);

}

export async function GetBusinessLocation(userContext: UserContext, isLocationControl: boolean, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessLocation;
    if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async function (position) {
            var latitude = position.coords.latitude;
            var longitude = position.coords.longitude;
            await AsyncGetLocation(userContext, latitude, longitude, "", url, isLocationControl, onComplete, onFailure);
        }, await function (error) {
            fetch('https://geolocation-db.com/json/')
                .then(response => response.json())
                .then(data => {
                    AsyncGetLocation(userContext, 0, 0, data.IPv4, url, isLocationControl, onComplete, onFailure);
                })
                .catch(error => AsyncGetLocation(userContext, 0, 0, "", url, isLocationControl, onComplete, onFailure))
        });
    }
    else {
        await fetch('https://geolocation-db.com/json/')
            .then(response => response.json())
            .then(data => {
                AsyncGetLocation(userContext, 0, 0, data.IPv4, url, isLocationControl, onComplete, onFailure);
            })
            .catch(error => AsyncGetLocation(userContext, 0, 0, "", url, isLocationControl, onComplete, onFailure))
    }
}

export async function GetBusinessName(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessName + "?IsEditor=" + IsEditor;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetHouseCallCity(userContext: UserContext, businessId: string, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getHouseCallCity;
    await AsyncGetBusinessHouseCallCity(userContext, businessId, url, onComplete, onFailure);
}


export async function GetBusinessPackages(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    if (!userContext.allowMultiLoc)
        userContext.allowMultiLoc = false;
    await ValidateToken(userContext);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getBusinessPackages + "?IsEditor=" + IsEditor + "&IsAllowMultilocation=" + userContext.allowMultiLoc;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBusinessReviewPecentage(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + userContext.businessId + VagaroAPIEnum.getReviewPercentage;
    await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
}

export async function GetBusinessCart(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    var LoginDetails = utils.ReadCookie();

    var encsitebid = "";
    if (userContext.sitebid !== undefined && userContext.sitebid !== null && userContext.sitebid !== "") {
        encsitebid = userContext.sitebid;
    } else {
        encsitebid = userContext.businessId;
    }

    if (LoginDetails.EncUid !== undefined && LoginDetails.EncUid !== "" && LoginDetails.EncUid !== null) {
        const url = encsitebid + VagaroAPIEnum.getCartCount + "?EncUid=" + LoginDetails.EncUid;
        await AsyncGetBusinessInformation(userContext, url, onComplete, onFailure);
    }
    else {
        if (onComplete) {
            onComplete("0");
        }
    }

}

export async function GetBusienssForm(siteData: UserContext, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {
    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + siteData.businessId + VagaroAPIEnum.getForms + "/" + (siteData.formid == "1" ? "659fc093145116743f6fd103" : (utils.isNotEmptyOrWhiteSpace(siteData.formid) ? siteData.formid : "659fc093145116743f6fd103"));
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);
}

export async function GetBusinessFormList(siteData: UserContext, location: string, onComplete?: (data: any) => void, onFailure?: (data: any) => void) {

    await ValidateToken(siteData);
    const url = VagaroAPIEnum.getMerchantRoot + location + VagaroAPIEnum.getFormList;
    await AsyncGetBusinessInformation(siteData, url, onComplete, onFailure);

}


export async function getClustorBusinessData(userContext: UserContext, Operation: string, Name: string, Category: string, MultiGroupString: string, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getClustorBusinessData;
    const content = {
        "Operation": Operation,
        "Name": Name,
        "Category": Category,
        "MultiGroupString": MultiGroupString
    }
    await AsyncAjaxPost(url, JSON.stringify(content), userContext.accessToken, onComplete, onFailure, userContext.businessId);
}

export async function GetBusinessHero_NickName(siteData: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(siteData);
    var IsEditor = (window as any).isEditor !== undefined ? (window as any).isEditor : false;
    let url = "";
    let previewCluster = GetPreviewCluster();

    if (previewCluster && previewCluster.length > 0) {
        url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessHeroNickName + "?IsEditor=false&NickName=" + encodeURIComponent(previewCluster);
    }

    else if (IsEditor) {
        url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessHeroNickName + "?IsEditor=true";
    }
    else {
        url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getBusinessHeroNickName + "?IsEditor=false&NickName=" + encodeURIComponent(siteData.nickname ?? "");
    }
    await AsyncChache(
        ChacheMethod.GET,
        url,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }
        },
        siteData
    );

}


export async function GetListingOnlyBusinesses(siteData: UserContext, onComplete?: Function, onFailure?: Function, pageNumber?: string, pageSize?: string, search?: string, serviceSearch?: string, ignoreNickName?: boolean, forceNickName?: string, bType?: any[], forceConsumerMode?: boolean) {
    await ValidateToken(siteData);
    let IsEditor = forceConsumerMode ? false : ((window as any).isEditor !== undefined ? (window as any).isEditor : false);



    let previewCluster = GetPreviewCluster();
    let url = "";

    //default to page 1 and 100 items per page to avoid server parsing error of empty strings
    let Editor = false;
    let nickName = "";
    let pgno = "1";
    let pgsize = "100";
    let searchtx = "";
    url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getListingOnlyBusinesses;
    if (IsEditor) {
        Editor = true;
        pgno = (pageNumber && pageSize) ? pageNumber : "1";
        pgsize = (pageNumber && pageSize) ? pageSize : "100";
        //url =  VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getListingOnlyBusinesses + "?IsEditor=true"+pageParams;
    }
    else if (forceNickName && forceNickName.length > 0) {
        nickName = encodeURIComponent(forceNickName)
        Editor = false;
        //url =  VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getListingOnlyBusinesses + "?IsEditor=false&NickName=" +nickName;
    }

    else if (previewCluster && previewCluster.length > 0) {
        if (!ignoreNickName) {
            let previewCluster = GetPreviewCluster();
            nickName = encodeURIComponent(previewCluster)
        }
        Editor = false;

        //url =  VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getListingOnlyBusinesses + "?IsEditor=false&NickName=" +nickName;
    }

    else {

        if (!ignoreNickName) {
            nickName = encodeURIComponent(siteData.nickname ?? "")
        }
        Editor = false;
        pgno = (pageNumber && pageSize) ? pageNumber : "1";
        pgsize = (pageNumber && pageSize) ? pageSize : "100";
        searchtx = (search ? search : "");
        //url =  VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getListingOnlyBusinesses + "?IsEditor=false&NickName=" + nickName+pageParams + (search?"&SearchText="+search:"");
    }

    const Requestdata: any = {};
    Requestdata.IsEditor = Editor;
    Requestdata.NickName = nickName;
    Requestdata.PageNumber = pgno;
    Requestdata.PageSize = pgsize;
    Requestdata.SearchText = searchtx;
    Requestdata.bType = bType;
    if (serviceSearch)
        Requestdata.SearchService = serviceSearch;

    await AsyncChache(
        ChacheMethod.POST,
        url,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }
        },
        siteData,
        Requestdata
    );

}

export async function GetAdminServices(userContext: UserContext, onComplete?: Function, onFailure?: Function) {

    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getAdminServices;
    await AsyncChache(
        ChacheMethod.GET,
        url,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }
        },
        userContext
    );

}

export async function GetFacilityFilters(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getFacilityFilters;
    await AsyncChache(
        ChacheMethod.GET,
        url,
        function onSuccess(data: any) {
            if (onComplete) {
                const newData = data?.data?.Data ?? null;
                onComplete(newData);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }
        },
        userContext,
        undefined,
        true,
        PublicAsyncAjaxGet
    );
}

export async function GetAdminSearchServices(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.getAdminSearchServices;
    await AsyncChache(
        ChacheMethod.GET,
        url,
        function onSuccess(data: any) {
            if (onComplete) {
                const raw: any[] = data?.data?.Data ?? [];
                const mapped = raw
                    .filter((cat: any) => cat.children?.length > 0)
                    .map((cat: any) => ({
                        category: cat.text,
                        countryID: cat.CountryID ?? 0,
                        service: cat.children.map((child: any) => ({
                            text: child.text,
                            countryID: child.CountryID ?? 0,
                        })),
                    }));
                onComplete(mapped);
            }
        },
        function OnError(data: any) {
            if (onFailure) {
                onFailure(data);
            }
        },
        userContext,
        undefined,
        true,
        PublicAsyncAjaxGet
    );
}

export async function GetCommonReponse(userContext: UserContext, onComplete?: Function, onFailure?: Function) {
    await ValidateToken(userContext);
    await CommonReponse(userContext, onComplete, onFailure);
}

export interface NicknameLinkData {
    LocationNickName: string;
    URLSlug: string;
}

export async function GetNicknameLinkData(
    siteData: UserContext,
    onComplete?: Function,
    onFailure?: Function
) {
    // Guard: encryptMID is required for this API call
    // It's only available on renter/location pages, not on owner/main pages
    if (!siteData.encryptMID) {
        onComplete?.(null);
        return;
    }

    const url = VagaroAPIEnum.getMerchantRoot + VagaroAPIEnum.getNicknameURL;
    await AsyncGetNicknameURL(siteData, url, onComplete, onFailure);
}

// Type for nearby location data
// export type NearbyLocationData = {
//     businessID: number;
//     encBID: string;
//     bidHash: string;
//     businessName: string;
//     latitude: number;
//     longitude: number;
//     distance: number;
//     street: string;
//     streetNo: string;
//     city: string;
//     stateCode: string;
//     stateName: string;
//     zip: string;
//     countryID: number;
//     vagaroURL: string;
//     cityCount: number;
//     mobileServiceRadius: number;
//     cdn: string;
//     alias: string;
//     nickName?: string;
//     isMobileServiceMandatory: number;
//     hasOutCallService: boolean;
//     isSelected: boolean;
//     isGroupMysite: boolean;
//     isLocationNickName: boolean;
// };

// Type for location data
export type NearbyLocationData = {
    externalLocationId: string;
    address: string;
    streetNo: string;
    city: string;
    state: string;
    distance: number;
    stateCode: string;
    countryId: number;
    zip: string;
    locationNickName: string;
    image: string;
    location: {
        type: string;
        coordinates: [number, number];
    };
    includeInMysite: boolean;
    urlSlug: string;
    isActive: boolean;
    franchiseeName: string;
    phoneNo: string;
    emailAddress: string;
    franchiseeEmail: string;
    imageUrls: string[];
    socialUrls: Array<{
        name: string;
        url: string;
    }>;
    customFields: any[];
    updatedAt: string;
};



// Squared distance for efficient sorting (no sqrt needed)
function squaredDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const dx = lat2 - lat1;
    const dy = lon2 - lon1;
    return dx * dx + dy * dy;
}

// Haversine formula for accurate distance on Earth's surface
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number, useMiles: boolean): number {
    const R = useMiles ? 3958.8 : 6371; // Earth's radius in miles or km
    const toRad = (deg: number) => deg * Math.PI / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}

/**
 * GetNearbyLocations - Fetches and processes nearby locations for display
 * Flow: Fetch all locations → Find current → Filter → Sort by distance → Slice
 */
export async function GetNearbyLocations(
    siteData: UserContext,
    locationCount: number,
    onComplete?: Function,
    onFailure?: Function
) {


    GetBusinessHero_NickName(siteData, (data: any) => {

        let latLong = data?.location?.coordinates;

        if (!latLong || latLong.length < 2) {
            onFailure && onFailure("Failed to get current location nickname");
            return;
        }
        let url = VagaroAPIEnum.getNearbyLocation + `?longitude=${latLong[0]}&latitude=${latLong[1]}`;;


        AsyncAjaxGet(
            url,
            siteData.accessToken,
            (data: any) => {

                let locations = data?.data?.data;
                if (!locations || locations.length === 0) {
                    onFailure && onFailure("No nearby locations found");
                    return;
                }

                locations.forEach((loc: NearbyLocationData) => {
                    loc.distance = haversineDistance(
                        latLong[1],
                        latLong[0],
                        loc.location.coordinates[1],
                        loc.location.coordinates[0],
                        loc.countryId === 1 // US uses miles, others use km
                    );
                });
                locations = locations.filter((l: NearbyLocationData) => {

                    return l.distance > 0;

                })

                onComplete && onComplete(locations.slice(0, locationCount));

            },
            (error: any) => {
                onFailure && onFailure("Failed to get nearby locations");

            },
            siteData.encryptMID,


        )
    }, (error: any) => {
        onFailure && onFailure("Failed to get current location nickname");
    });

}


// --- Public Search APIs (for Real-Time Availability cross-location search) ---

export interface LocationSearchResult {
    locations: Array<{ CityState: string; place_id: string; countryName: string }>;
    businesses: Array<{ businessID: string; businessName: string; vagaroURL: string; cityState: string }>;
    totalrecords?: number;
    promo?: string;
}

export interface PlaceLocationData {
    latitude: number;
    longitude: number;
    zipCode: string;
    cityState: string;
    place_id: string;
    countryID: number;
    countryName: string;
    city: string;
    state: string;
    stateCode: string;
    locationDetail: string;
    currencySymbol: string;
    utcOffset: number;
    timeZoneOffSet: number;
    IsSupportDayLight: boolean;
}

export interface SearchListingResult {
    merchantId: string;
    ownerName: string;
    ownerPhoto: string;
    businessName: string;
    description: string;
    businessImagePath: string;
    facilityValue: string;
    vagaroURL: string;
    city: string;
    stateName: string;
    reviewTotal: number;
    averageRank: number;
    serviceProviderID: number;
    freeTime: string;
    appointmentDate: string;
    FromTime: string;
    serviceID: number;
    encServiceId: string;
    price: number;
    isShowPriceOnline: boolean;
    latitude: number;
    longitude: number;
    distance: string;
    telephone: string;
    stateCode: string;
    strDistance: string;
    strFreeTime: string;
    sGroup: string;
    serviceTitle: string;
    eventType: number;
    redirectURL: string;
    redirectAboutPageURL: string;
    stateCity: string;
    street: string;
    zip: string;
    isdormant: boolean;
    locationName?: string;
    mappingURL?: string;
    studioNumber?: string;
}

export interface SearchListingResponse {
    elasticService: string;
    sPagingDetails: string;
    totalPage: number;
    totalrecords: number;
    promo: string;
    response: SearchListingResult[];
}

export async function SearchLocations(
    userContext: UserContext,
    searchText: string,
    latitude: number,
    longitude: number,
    pageIndex: number,
    promo: string,
    onComplete?: Function,
    onFailure?: Function
) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.searchLocationSearch + "?pageIndex=" + pageIndex;
    const payload = {
        searchText,
        latitude,
        longitude,
        IncludeBusiness: true,
        promo,
    };
    await PublicAsyncAjaxPost(
        url,
        payload,
        userContext.accessToken,
        function onSuccess(data: any) {
            const result = data?.data?.Data ?? null;
            if (onComplete) onComplete(result);
        },
        function onError(data: any) {
            console.error("[RTA] SearchLocations failed:", data);
            if (onFailure) onFailure(data);
        }
    );
}

export async function GetLocationDetailsByPlaceID(
    userContext: UserContext,
    placeID: string,
    onComplete?: Function,
    onFailure?: Function
) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.searchLocationPlace + "?placeID=" + encodeURIComponent(placeID);
    await PublicAsyncAjaxGet(
        url,
        userContext.accessToken,
        function onSuccess(data: any) {
            const result = data?.data?.Data ?? null;
            if (onComplete) onComplete(result);
        },
        function onError(data: any) {
            console.error("[RTA] GetLocationDetailsByPlaceID failed:", data);
            if (onFailure) onFailure(data);
        }
    );
}

/** Server-side sort type values for VagaroSearchPayload.sortType */
export const SortType = {
    Nearest: 5,
    Earliest:11,
    EndingSoon: 8,
    HighestCost: 9,
    LowestCost: 10,
    HighestRated: 3,
} as const;

export type SortTypeValue = (typeof SortType)[keyof typeof SortType];

export interface VagaroSearchPayload {
    type: string;
    sortType: number;
    latlong: string;
    countryID: number | string;
    searchFilter: number;
    arrSearchFilter: string;
    cityName: string;
    stateCode: string;
    sPagingDetails: string;
    vagaroServiceIDs?: string;
    // Optional facility filters applied from the Real Time Availability drawer
    facilitiesvalues?: {
        HID: string;
        VID: string;
        TP: string;
        value: string;
    }[];
    searchText?: string;
    serviceDate?: string;
    serviceTime?: string;
    // Search radius in miles — live API always sends 50 for service/pro searches
    Radius?: number;
    // Optional slug for single-location scoping (Layout 1 server search)
    URLSlug?: string;
    // TODO(pre-release): Remove — temporary promo param for testing
    promo?: string;
}

export async function VagaroSearchBusiness(
    userContext: UserContext,
    payload: VagaroSearchPayload,
    pageIndex: number,
    pageSize: number,
    onComplete?: Function,
    onFailure?: Function
) {
    await ValidateToken(userContext);
    const url = VagaroAPIEnum.searchListing + "?currentPageIndex=" + pageIndex + "&pageSize=" + pageSize;
    await PublicAsyncAjaxPost(
        url,
        payload,
        userContext.accessToken,
        function onSuccess(data: any) {
            const result = data?.data?.Data ?? null;
            if (onComplete) onComplete(result);
        },
        function onError(data: any) {
            console.error("[RTA] VagaroSearchBusiness failed:", data);
            if (onFailure) onFailure(data);
        }
    );
}

export { GetBusinessServices, GetBusinessServiceCategories, GetBusinessClassCategories };